import { apiClient } from '../../../api/client'
import { downloadXlsx } from '../../../lib/download'
import type {
  PagedResponse,
  StaticOption,
  TemplateParamGroupOption,
  TemplateParameter,
  TemplateParameterOption,
  TemplateParameterUpsert,
} from '../types'

const BASE = '/management/template-parameters'

/*
 * **Ekleme / silme / durum sarmalayıcısı yoktur** (2026-08-04 kararı — gruplarla aynı gerekçe):
 * parametreler şablon metnindeki yer tutucuların kataloğudur; kod ile eşleşmesi gereken bir referans
 * kümesidir, ekranda yalnız **düzenlenir**. Sunucudaki `POST` / `DELETE` / `PUT /{id}/status` uçları
 * yerinde durur (bilinçli tercih), ama istemci bunları çağırmaz — çağrılmayan bir sarmalayıcı,
 * ekranda olmayan bir yeteneğin var olduğu izlenimini bırakır.
 */

export interface TemplateParameterQuery {
  /** Grup filtresi; `null` = Tümü → gönderilmez. */
  groupId?: number | null
  page: number
  size: number
  /** Kolon sıralaması (`alan,asc|desc`); yoksa gönderilmez → sunucu varsayılanı (grup, kimlik). */
  sort?: string
}

/**
 * Parametre listesi (059) — **sunucu-tarafı** sayfalama + grup filtresi + **kolon sıralaması**.
 * `sort` `useTableSort`'tan gelir; sunucu beyaz listesinde olmayan alan sessizce düşürülür.
 */
export async function getTemplateParameters({
  groupId,
  page,
  size,
  sort,
}: TemplateParameterQuery): Promise<PagedResponse<TemplateParameter>> {
  const { data } = await apiClient.get<PagedResponse<TemplateParameter>>(BASE, {
    params: { page, size, groupId: groupId ?? undefined, sort },
  })
  return data
}

/** Tekil parametre — form açılışı. */
export async function getTemplateParameter(id: number): Promise<TemplateParameter> {
  const { data } = await apiClient.get<TemplateParameter>(`${BASE}/${id}`)
  return data
}

/** Grup combo seçenekleri — yalnız aktif gruplar. */
export async function getTemplateParameterGroupOptions(): Promise<TemplateParamGroupOption[]> {
  const { data } = await apiClient.get<TemplateParamGroupOption[]>(`${BASE}/group-options`)
  return data
}

/** "Statik mi?" seçenekleri; `yes` bayrağı statik içerik alanını açar/kapatır. */
export async function getStaticOptions(): Promise<StaticOption[]> {
  const { data } = await apiClient.get<StaticOption[]>(`${BASE}/static-options`)
  return data
}

/** Katalog — şablon formundaki panelle **aynı** veri, parametre yetkisinin kapısından. */
export async function getTemplateParameterOptions(): Promise<TemplateParameterOption[]> {
  const { data } = await apiClient.get<TemplateParameterOption[]>(`${BASE}/options`)
  return data
}

/**
 * Parametre güncelleme; `modDate` optimistic lock için zorunludur.
 *
 * Gövde **grup** ve **parametre kodunu** değişmemiş hâlleriyle taşır (sunucu sözleşmesi onları
 * zorunlu tutar) — ekranda bu iki alan salt-okunurdur, kullanıcı yalnız ad/açıklama/statik alanlarını
 * değiştirir.
 */
export async function updateTemplateParameter(
  id: number,
  body: TemplateParameterUpsert,
): Promise<TemplateParameter> {
  const { data } = await apiClient.put<TemplateParameter>(`${BASE}/${id}`, body)
  return data
}

/**
 * Şablon parametrelerini Excel (.xlsx) olarak indirir.
 *
 * Liste ucuyla aynı filtreler; sunucu **tüm** filtreli kayıtları döndürür (sayfalama yok). İndirme
 * mekaniği ve dosya adı ortak {@link downloadXlsx} yardımcısındadır (boş filtreler gönderilmez; ad
 * sunucunun Content-Disposition'ından okunur).
 */
export async function exportTemplateParameters(groupId?: number, sort?: string): Promise<void> {
  await downloadXlsx(`${BASE}/export`, { groupId, sort }, 'sablon-parametreleri.xlsx')
}
